import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Add auth token to requests (only if not already set)
api.interceptors.request.use((config) => {
  // Don't override if Authorization header is already set (e.g., for partner routes)
  if (!config.headers.Authorization) {
    const currentPath = window.location.pathname;
    
    // Check for validator token first (for validator routes)
    const validatorToken = localStorage.getItem('validatorToken') || localStorage.getItem('validator_token');
    const isValidatorRoute = currentPath.startsWith('/validador') || currentPath.startsWith('/validator');
    
    // Check for press token (for press routes)
    const pressToken = localStorage.getItem('press_token');
    const isPressRoute = currentPath.startsWith('/press') || currentPath.startsWith('/prensa');
    
    // Check for producer token (for producer routes)
    const producerToken = localStorage.getItem('producer_token');
    const isProducerRoute = currentPath.startsWith('/producer');
    
    // Check for partner token (for partner routes)
    const partnerToken = localStorage.getItem('partner_token');
    const isPartnerRoute = currentPath.startsWith('/partner');
    
    // Check for admin routes
    const isAdminRoute = currentPath.startsWith('/admin');
    const adminToken = localStorage.getItem('token');
    
    if (validatorToken && isValidatorRoute) {
      config.headers.Authorization = `Bearer ${validatorToken}`;
    } else if (pressToken && isPressRoute) {
      config.headers.Authorization = `Bearer ${pressToken}`;
    } else if (producerToken && isProducerRoute) {
      config.headers.Authorization = `Bearer ${producerToken}`;
    } else if (partnerToken && isPartnerRoute) {
      config.headers.Authorization = `Bearer ${partnerToken}`;
    } else if (adminToken && isAdminRoute) {
      // Admin routes - must use admin token
      config.headers.Authorization = `Bearer ${adminToken}`;
    } else if (adminToken) {
      // Fallback to admin token for any other route
      config.headers.Authorization = `Bearer ${adminToken}`;
    } else {
      // Fallback to public token (for Safari/iOS where cookies don't work)
      const publicToken = localStorage.getItem('public_token');
      if (publicToken) {
        config.headers.Authorization = `Bearer ${publicToken}`;
      }
    }
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Normalize FastAPI validation errors so callers can safely render
    // `err.response.data.detail` as text without crashing React (error #31).
    // FastAPI returns 422 with `detail: [{type, loc, msg, input, ctx, url}, ...]`
    // — an array of objects — which explodes when passed as JSX children or
    // to toast.error. We collapse it into a human-readable string, while
    // preserving the original list under `detail_raw` for anyone who needs it.
    try {
      const data = error?.response?.data;
      if (data && data.detail !== undefined && data.detail !== null && typeof data.detail !== 'string') {
        const toReadable = (item) => {
          if (item == null) return '';
          if (typeof item === 'string') return item;
          if (typeof item === 'object') {
            if (typeof item.msg === 'string') {
              const field = Array.isArray(item.loc) ? item.loc.filter(p => p !== 'body').join('.') : '';
              return field ? `${field}: ${item.msg}` : item.msg;
            }
            if (typeof item.message === 'string') return item.message;
            try { return JSON.stringify(item); } catch (_) { return String(item); }
          }
          return String(item);
        };
        const raw = data.detail;
        const flat = Array.isArray(raw) ? raw.map(toReadable).filter(Boolean).join(' · ') : toReadable(raw);
        data.detail_raw = raw;
        data.detail = flat || 'Datos inválidos';
      }
    } catch (_) { /* never let the normalizer break error handling */ }

    // Handle 401 errors - redirect to login for the appropriate role.
    // IMPORTANT: we only redirect when the failing endpoint truly belongs to
    // that role (e.g. /press/me → press, /producers/me → producer).
    // 401s from generic endpoints like /public-auth/me must be ignored — they
    // simply mean "no public user logged in" and are a normal background check
    // on many pages that should NOT log out a role user.
    if (error.response?.status === 401) {
      const requestUrl = error.config?.url || '';
      const currentPath = window.location.pathname;

      // Normalize the request URL for prefix checks (it can be relative or absolute).
      const reqPath = (() => {
        try {
          if (requestUrl.startsWith('http')) return new URL(requestUrl).pathname;
          // baseURL is /api so the configured url is usually like '/press/me'.
          return requestUrl.startsWith('/api/') ? requestUrl.slice(4) : requestUrl;
        } catch (_) { return requestUrl; }
      })();

      const isRoleEndpoint = {
        press:     reqPath.startsWith('/press/'),
        producer:  reqPath.startsWith('/producers/') || reqPath.startsWith('/venues/producer'),
        partner:   reqPath.startsWith('/partners/'),
        validator: reqPath.startsWith('/validators/') || reqPath.startsWith('/validator/'),
        admin:     reqPath.startsWith('/admin/') || reqPath.startsWith('/permissions/') || reqPath.startsWith('/settings/admin'),
      };

      const isAdminRoute = currentPath.startsWith('/admin');
      const isPressRoute = currentPath.startsWith('/press') || currentPath.startsWith('/prensa');
      const isProducerRoute = currentPath.startsWith('/producer') || currentPath.startsWith('/productora');
      const isPartnerRoute = currentPath.startsWith('/partner');
      const isValidatorRoute = currentPath.startsWith('/validador') || currentPath.startsWith('/validator');

      const isLoginPage = (
        currentPath === '/admin/login' ||
        currentPath === '/prensa/login' ||
        currentPath === '/press/login' ||
        currentPath === '/producer/login' ||
        currentPath === '/partner/login' ||
        currentPath === '/validador/login'
      );

      const scheduleRedirect = (target, tokenKeys) => {
        if (window._roleRedirectScheduled) return;
        window._roleRedirectScheduled = true;
        console.warn(`[API] 401 on ${reqPath} from ${currentPath}. Token expired/invalid — redirecting to ${target}.`);
        tokenKeys.forEach(k => localStorage.removeItem(k));
        try {
          window.dispatchEvent(new CustomEvent('auth:session-expired', { detail: { target, requestUrl: reqPath } }));
        } catch (_) {}
        setTimeout(() => {
          window._roleRedirectScheduled = false;
          window.location.href = target;
        }, 400);
      };

      if (!isLoginPage) {
        // Match role-specific 401 → role login. Skip generic endpoints (e.g. /public-auth/me).
        if (isPressRoute && isRoleEndpoint.press) {
          scheduleRedirect('/prensa/login', ['press_token', 'press_data']);
        } else if (isProducerRoute && isRoleEndpoint.producer) {
          scheduleRedirect('/producer/login', ['producer_token']);
        } else if (isPartnerRoute && isRoleEndpoint.partner) {
          scheduleRedirect('/partner/login', ['partner_token']);
        } else if (isValidatorRoute && isRoleEndpoint.validator) {
          scheduleRedirect('/validador/login', ['validatorToken', 'validator_token']);
        } else if (isAdminRoute && isRoleEndpoint.admin) {
          if (!window._adminRedirectScheduled) {
            window._adminRedirectScheduled = true;
            console.warn('[API] Admin 401 detected, redirecting to login. URL:', reqPath);
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            localStorage.removeItem('public_token');
            setTimeout(() => {
              window._adminRedirectScheduled = false;
              window.location.href = '/admin/login';
            }, 300);
          }
        }
      }
    }
    return Promise.reject(error);
  }
);

// Auth (Admin)
export const authApi = {
  login: (username, password) => api.post('/auth/login', { username, password }),
  getMe: () => api.get('/auth/me'),
  changePassword: (oldPassword, newPassword) => 
    api.post(`/auth/change-password?old_password=${oldPassword}&new_password=${newPassword}`),
};

// Public Auth (Users)
export const publicAuthApi = {
  initRegister: (email) => api.post(`/public-auth/register/init?email=${encodeURIComponent(email)}`),
  verifyEmail: (email, code) => api.post('/public-auth/register/verify', { email, code }),
  completeRegister: (data) => api.post('/public-auth/register/complete', data),
  directRegister: (data) => api.post('/public-auth/register/direct', data),
  login: (email, password) => api.post('/public-auth/login', { email, password }),
  googleSession: (sessionId) => api.post('/public-auth/google/session', { session_id: sessionId }),
  googleCallback: (credential) => api.post('/public-auth/google/callback', { credential }),
  completeGoogleProfile: (dni, birthDate) => 
    api.post(`/public-auth/google/complete-profile?dni=${dni}&birth_date=${birthDate}`),
  getMe: () => api.get('/public-auth/me'),
  updateProfile: (data) => api.put('/public-auth/me', data),
  logout: () => api.post('/public-auth/logout'),
  getLinkedAccounts: () => api.get('/public-auth/linked-accounts'),
  portalLogin: (portalType) => api.post(`/public-auth/portal-login/${portalType}`),
};

// Public Users
export const publicUsersApi = {
  getMyTickets: () => api.get('/public-users/my-tickets'),
  // Admin functions
  adminList: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.search) queryParams.append('search', params.search);
    if (params.subscription_only) queryParams.append('subscription_only', 'true');
    if (params.auth_provider) queryParams.append('auth_provider', params.auth_provider);
    if (params.sort_by) queryParams.append('sort_by', params.sort_by);
    if (params.segment) queryParams.append('segment', params.segment);
    if (params.min_bravx_points) queryParams.append('min_bravx_points', params.min_bravx_points);
    if (params.min_spent) queryParams.append('min_spent', params.min_spent);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.skip) queryParams.append('skip', params.skip);
    return api.get(`/public-users/admin/list?${queryParams.toString()}`);
  },
  adminGetUser: (userId) => api.get(`/public-users/admin/${userId}`),
  adminUpdateUser: (userId, data) => api.put(`/public-users/admin/${userId}`, data),
  adminToggleSubscription: (userId, action, months = 1) => 
    api.post(`/public-users/admin/${userId}/subscription?action=${action}&months=${months}`),
  adminDeleteUser: (userId) => api.delete(`/public-users/admin/${userId}`),
  adminResetPassword: (userId, newPassword) => 
    api.post(`/public-users/admin/${userId}/reset-password?new_password=${encodeURIComponent(newPassword)}`),
  adminSendVerificationEmail: (userId) => 
    api.post(`/public-users/admin/${userId}/send-verification`),
  // Marketing
  sendMarketingEmail: (data) => api.post('/public-users/admin/send-marketing-email', data),
};

// Subscriptions (FULL BRAVX)
export const subscriptionApi = {
  getSettings: () => api.get('/subscriptions/settings'),
  updateSettings: (data) => api.put('/subscriptions/settings', data),
  subscribe: (data) => api.post('/subscriptions/subscribe', data),
  getMySubscription: () => api.get('/subscriptions/my-subscription'),
  cancelSubscription: () => api.post('/subscriptions/cancel'),
  getCoupons: () => api.get('/subscriptions/coupons'),
  createCoupon: (data) => api.post('/subscriptions/coupons', data),
  useFreeDownload: (photoId) => api.post(`/subscriptions/use-free-download?photo_id=${photoId}`),
  // Free Trial
  startFreeTrial: () => api.post('/subscriptions/start-free-trial'),
  getTrialStatus: () => api.get('/subscriptions/trial-status'),
  // Partner Benefits
  getPartnerBenefits: () => api.get('/subscriptions/partner-benefits'),
  getMyPartnerBenefits: () => api.get('/subscriptions/partner-benefits/me'),
  getBenefitCode: (benefitId) => api.get(`/subscriptions/partner-benefits/${benefitId}/code`),
  getAllPartnerBenefitsAdmin: () => api.get('/subscriptions/partner-benefits/admin/all'),
  createPartnerBenefit: (data) => api.post('/subscriptions/partner-benefits', data),
  updatePartnerBenefit: (id, data) => api.put(`/subscriptions/partner-benefits/${id}`, data),
  deletePartnerBenefit: (id) => api.delete(`/subscriptions/partner-benefits/${id}`),
  // Notifications
  getNotifications: () => api.get('/subscriptions/notifications'),
  markNotificationsRead: () => api.post('/subscriptions/notifications/mark-read'),
  // Payment Methods & Rebill
  getPaymentMethods: () => api.get('/subscriptions/payment-methods'),
  getRebillConfig: () => api.get('/subscriptions/rebill/config'),
  subscribeWithRebill: (data) => api.post('/subscriptions/subscribe/rebill', data),
  verifyRebillPayment: (subscriptionId) => api.post(`/subscriptions/rebill/verify-payment/${subscriptionId}`),
  cancelSubscription: () => api.post('/subscriptions/rebill/cancel'),
  reactivateSubscription: () => api.post('/subscriptions/rebill/reactivate'),
  getSavedCards: () => api.get('/subscriptions/rebill/cards'),
  deleteCard: (cardId) => api.delete(`/subscriptions/rebill/cards/${cardId}`),
  setPrimaryCard: (cardId) => api.post(`/subscriptions/rebill/cards/${cardId}/set-primary`),
};

// Auras Management
export const aurasApi = {
  getPresets: (includeInactive = false) => api.get(`/auras/presets?include_inactive=${includeInactive}`),
  createPreset: (data) => api.post('/auras/presets', data),
  updatePreset: (id, data) => api.put(`/auras/presets/${id}`, data),
  deletePreset: (id) => api.delete(`/auras/presets/${id}`),
  reorderPresets: (orderData) => api.put('/auras/presets/reorder', orderData),
};

// Internal Calendar
export const calendarApi = {
  getEvents: (start, end, eventType) => {
    const params = new URLSearchParams();
    if (start) params.append('start', start);
    if (end) params.append('end', end);
    if (eventType) params.append('event_type', eventType);
    return api.get(`/calendar/events?${params.toString()}`);
  },
  createEvent: (data) => api.post('/calendar/events', data),
  updateEvent: (id, data) => api.put(`/calendar/events/${id}`, data),
  deleteEvent: (id) => api.delete(`/calendar/events/${id}`),
  toggleComplete: (id) => api.post(`/calendar/events/${id}/toggle-complete`),
  addAttachment: (id, attachment) => api.post(`/calendar/events/${id}/attachments`, attachment),
};

// Roles (Owner only)
export const rolesApi = {
  getAll: () => api.get('/roles/'),
  create: (data) => api.post('/roles/', data),
  update: (id, data) => api.put(`/roles/${id}`, data),
  delete: (id) => api.delete(`/roles/${id}`),
  getPermissions: () => api.get('/roles/permissions'),
};

// Users (Owner only)
export const usersApi = {
  getAll: () => api.get('/users/'),
  create: (data) => api.post('/users/', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  resetPassword: (userId, newPassword) => 
    api.post(`/users/${userId}/reset-password?new_password=${newPassword}`),
};

// Activity Logs (Owner only)
export const logsApi = {
  getAll: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.user_id) queryParams.append('user_id', params.user_id);
    if (params.action) queryParams.append('action', params.action);
    if (params.resource_type) queryParams.append('resource_type', params.resource_type);
    return api.get(`/logs/?${queryParams.toString()}`);
  },
};

// Site Settings (Owner only for edit, public for read)
export const adminRepairsApi = {
  relinkTickets: () => api.post('/tickets/admin/repairs/relink-tickets'),
  resendDjApprovalEmails: () => api.post('/dj/admin/repairs/resend-approval-emails'),
};

export const settingsApi = {
  getSitePublic: () => api.get('/settings/site/public'),
  getSite: () => api.get('/settings/site'),
  updateSite: (data) => api.put('/settings/site', data),
  // Terms and Conditions
  getTerms: () => api.get('/settings/terms'),
  updateTerms: (data) => api.put('/settings/terms', data),
  // About Us / Quiénes Somos
  getAbout: () => api.get('/settings/about'),
  updateAbout: (data) => api.put('/settings/about', data),
  // Application Forms
  getPartnerForm: () => api.get('/settings/form-partner'),
  getProducerForm: () => api.get('/settings/form-producer'),
};

// Events
export const eventsApi = {
  getPublic: (includePast = false) => api.get(`/events/?include_past=${includePast}`),
  getAll: () => api.get('/events/all'),
  getById: (id) => api.get(`/events/${id}`),
  getFullDetails: (id) => api.get(`/events/${id}/full`),
  create: (data) => api.post('/events/', data),
  update: (id, data) => api.put(`/events/${id}`, data),
  updateStatus: (id, status) => api.put(`/events/${id}/status?status=${status}`),
  delete: (id) => api.delete(`/events/${id}`),
  // Dashboard & Advanced Management
  getDashboard: (eventId) => api.get(`/events/${eventId}/dashboard`),
  getOrders: (eventId) => api.get(`/events/${eventId}/orders`),
  // Discount Codes
  getDiscountCodes: (eventId) => api.get(`/events/${eventId}/discount-codes`),
  createDiscountCode: (eventId, data) => api.post(`/events/${eventId}/discount-codes`, data),
  toggleDiscountCode: (eventId, codeId, isActive) => api.put(`/events/${eventId}/discount-codes/${codeId}?is_active=${isActive}`),
  deleteDiscountCode: (eventId, codeId) => api.delete(`/events/${eventId}/discount-codes/${codeId}`),
  validateDiscountCode: (eventId, code) => api.get(`/events/${eventId}/discount-codes/validate/${code}`).then(res => res.data),
  // Ticket Tiers
  getTicketTiers: (eventId) => api.get(`/events/${eventId}/tiers`),
  getTicketTiersPublic: (eventId) => api.get(`/events/${eventId}/tiers/public`),
  createTicketTier: (eventId, data) => api.post(`/events/${eventId}/tiers`, data),
  updateTicketTier: (eventId, tierId, data) => api.put(`/events/${eventId}/tiers/${tierId}`, data),
  deleteTicketTier: (eventId, tierId) => api.delete(`/events/${eventId}/tiers/${tierId}`),
  // Vendor Links
  getVendorLinks: (eventId) => api.get(`/events/${eventId}/vendors`),
  createVendorLink: (eventId, data) => api.post(`/events/${eventId}/vendors`, data),
  deleteVendorLink: (eventId, vendorId) => api.delete(`/events/${eventId}/vendors/${vendorId}`),
  // Invitations
  getInvitations: (eventId) => api.get(`/events/${eventId}/invitations`),
  sendInvitations: (eventId, data) => api.post(`/events/${eventId}/invitations`, data),
  resendInvitation: (eventId, invitationId) => api.post(`/events/${eventId}/invitations/${invitationId}/resend`),
  deleteInvitation: (eventId, invitationId) => api.delete(`/events/${eventId}/invitations/${invitationId}`),
  // Subevents
  getSubevents: (eventId) => api.get(`/events/${eventId}/subevents`),
  createSubevent: (eventId, data) => api.post(`/events/${eventId}/subevents`, data),
  updateFullPass: (eventId, price, quantity) => api.put(`/events/${eventId}/full-pass?price=${price}&quantity=${quantity}`),
  purchaseFullPass: (eventId, data) => api.post(`/events/${eventId}/purchase-full-pass`, data),
  // Reports (Admin)
  getReport: (eventId) => api.get(`/events/${eventId}/report`),
  sendReport: (eventId, email = null) => api.post(`/events/${eventId}/report/send${email ? `?email=${encodeURIComponent(email)}` : ''}`),
  getReportHistory: (eventId) => api.get(`/events/${eventId}/report/history`),
  // RRPP (Admin)
  getRRPP: (eventId) => api.get(`/events/${eventId}/rrpp`),
  createRRPP: (eventId, data) => api.post(`/events/${eventId}/rrpp`, data),
  deleteRRPP: (eventId, rrppId) => api.delete(`/events/${eventId}/rrpp/${rrppId}`),
  // Order Management (Admin)
  updateOrder: (eventId, orderId, data) => api.put(`/events/${eventId}/orders/${orderId}`, data),
  resendOrderTickets: (eventId, orderId) => api.post(`/events/${eventId}/orders/${orderId}/resend-tickets`),
  refundOrder: (eventId, orderId, partialAmount = null, reason = '') => 
    api.post(`/events/${eventId}/orders/${orderId}/refund?${partialAmount ? `partial_amount=${partialAmount}&` : ''}reason=${encodeURIComponent(reason)}`),
  deleteOrder: (eventId, orderId) => api.delete(`/events/${eventId}/orders/${orderId}`),
  // Event Status
  markCompleted: (eventId) => api.post(`/events/${eventId}/mark-completed`),
  republish: (eventId) => api.post(`/events/${eventId}/republish`),
  // Featured Events
  getFeaturedEvents: () => api.get('/settings/featured-events/active'),
  // Queue System
  checkQueue: (eventId, token) => api.get(`/events/${eventId}/queue/check?token=${token}`),
  joinQueue: (eventId) => api.post(`/events/${eventId}/queue/join`),
};

// Tickets
export const ticketsApi = {
  purchase: (data) => api.post('/tickets/purchase', data),
  getOrder: (orderId) => api.get(`/tickets/order/${orderId}`),
  verifyPayment: (orderId, paymentId = null) => api.post(`/tickets/verify-payment/${orderId}${paymentId ? `?payment_id=${paymentId}` : ''}`),
  cancelOrder: (orderId) => api.post(`/tickets/cancel-order/${orderId}`),
  getMyTickets: (email) => api.get(`/tickets/my-tickets?email=${email}`),
  getMyTicketsUser: () => api.get('/tickets/my-tickets-user'),
  getAll: () => api.get('/tickets/all'),
  getByEvent: (eventId) => api.get(`/tickets/event/${eventId}`).then(res => res.data),
  validate: (ticketCode, eventId = null) => api.post(`/tickets/validate/${ticketCode}${eventId ? `?event_id=${eventId}` : ''}`),
  validateByDni: (dni, eventId) => api.post('/tickets/validate-by-dni', null, { params: { dni, event_id: eventId } }),
  validateByQr: (qrData) => api.post('/tickets/validate-by-qr', null, { params: { qr_data: qrData } }),
  searchByDni: (dni, eventId = null) => api.get(`/tickets/search-by-dni/${dni}${eventId ? `?event_id=${eventId}` : ''}`),
};

// Gallery
export const galleryApi = {
  getAll: (eventId = null) => api.get(`/gallery/${eventId ? `?event_id=${eventId}` : ''}`),
  getDrive: () => api.get('/gallery/drive'),
  add: (data) => api.post('/gallery/', data),
  delete: (id) => api.delete(`/gallery/${id}`),
};

// Files
export const filesApi = {
  getAll: (eventId = null, fileType = null) => {
    const params = new URLSearchParams();
    if (eventId) params.append('event_id', eventId);
    if (fileType) params.append('file_type', fileType);
    return api.get(`/files/?${params.toString()}`);
  },
  create: (data) => api.post('/files/', data),
  delete: (id) => api.delete(`/files/${id}`),
};

// Stats
export const statsApi = {
  get: () => api.get('/stats'),
};

export const analyticsApi = {
  track: (page, referrer) => api.post('/analytics/track', { page, referrer, user_agent: navigator.userAgent }),
  getSummary: (days = 7) => api.get(`/analytics/summary?days=${days}`),
  getBilling: () => api.get('/analytics/billing'),
};

// Password Recovery
export const passwordApi = {
  forgotPassword: (email) => api.post('/public-auth/forgot-password', { email }),
  resetPassword: (email, code, new_password) => api.post('/public-auth/reset-password', { email, code, new_password }),
};

// Withdrawal / Arrepentimiento (Ley 24.240)
export const withdrawalApi = {
  getLegalText: () => api.get('/withdrawal/legal-text'),
  submitRequest: (data) => api.post('/withdrawal/request', data),
  // Admin
  getRequests: (status = null) => api.get(`/withdrawal/admin/requests${status ? `?status=${status}` : ''}`),
  getStats: () => api.get('/withdrawal/admin/stats'),
  approveRequest: (id, notes = null) => api.post(`/withdrawal/admin/requests/${id}/approve${notes ? `?admin_notes=${encodeURIComponent(notes)}` : ''}`),
  rejectRequest: (id, reason) => api.post(`/withdrawal/admin/requests/${id}/reject?rejection_reason=${encodeURIComponent(reason)}`),
};

export default api;
